import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@web/components/ui/select";
import type { ReaderWorkspace } from "../../../reader/reader-workspace";
import { readerFontOptions } from "../../../reader/reader-font-options";
import {
  SettingColorRow,
  SettingColorSwatch,
  SettingExpandableSelectRow,
  SettingGroup,
  SettingSelectRow,
  SettingStepper,
  SettingSwitchRow,
} from "./reader-settings-controls";
import type {
  ReaderMarginPadding,
  ReaderMarginPreset,
  ReaderMarginPresetId,
  ReaderTypographyPreset,
  ReaderTypographyPresetId,
  ReaderTypographySettings,
} from "./reader-settings-presets";

export function ThemeSettings({
  workspace,
  fullscreen,
  fullscreenSupported,
  onFullscreenToggle,
}: {
  workspace: ReaderWorkspace;
  fullscreen: boolean;
  fullscreenSupported: boolean;
  onFullscreenToggle: () => void;
}) {
  const preference = workspace.preference;
  const isDarkTheme = preference.theme === "dark";
  const textColor = isDarkTheme ? preference.darkTextColor : preference.lightTextColor;
  const backgroundColor = isDarkTheme ? preference.darkBackgroundColor : preference.lightBackgroundColor;

  return (
    <SettingGroup>
      <SettingColorRow label="主题">
        <SettingColorSwatch
          label="文字"
          value={textColor}
          fallback={isDarkTheme ? "#f5f5f5" : "#1f1f1f"}
          onChange={value => {
            void workspace.handlePreferenceChange(isDarkTheme ? { darkTextColor: value } : { lightTextColor: value });
          }}
        />
        <SettingColorSwatch
          label="背景"
          value={backgroundColor}
          fallback={isDarkTheme ? "#171717" : "#fffaf0"}
          onChange={value => {
            void workspace.handlePreferenceChange(isDarkTheme ? { darkBackgroundColor: value } : { lightBackgroundColor: value });
          }}
        />
      </SettingColorRow>
      <SettingSwitchRow label="启动全屏" checked={fullscreen} disabled={!fullscreenSupported} onToggle={onFullscreenToggle} />
    </SettingGroup>
  );
}

export function FontSettings({ workspace }: { workspace: ReaderWorkspace }) {
  const preference = workspace.preference;

  return (
    <SettingGroup>
      <SettingSelectRow label="字体">
        <Select value={preference.fontFamily} onValueChange={value => void workspace.handlePreferenceChange({ fontFamily: value })}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {readerFontOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingSelectRow>

      <SettingSelectRow label="字重">
        <Select value={String(preference.fontWeight)} onValueChange={value => void workspace.handlePreferenceChange({ fontWeight: Number(value) })}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="300">偏细 300</SelectItem>
            <SelectItem value="400">常规 400</SelectItem>
            <SelectItem value="500">中等 500</SelectItem>
            <SelectItem value="600">偏粗 600</SelectItem>
            <SelectItem value="700">加粗 700</SelectItem>
          </SelectContent>
        </Select>
      </SettingSelectRow>

      <SettingSelectRow label="简繁">
        <Select value={preference.script} onValueChange={value => void workspace.handlePreferenceChange({ script: value as typeof preference.script })}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="source">原文</SelectItem>
            <SelectItem value="simplified">简体</SelectItem>
            <SelectItem value="traditional">繁体</SelectItem>
          </SelectContent>
        </Select>
      </SettingSelectRow>
    </SettingGroup>
  );
}

export function TypographySettings({
  preference,
  presets,
  selectedPreset,
  expanded,
  onToggleExpanded,
  onPresetChange,
  onCustomTypographyChange,
  onIndentChange,
  onTextAlignChange,
  onTextAlignLastChange,
}: {
  preference: ReaderWorkspace["preference"];
  presets: Record<ReaderTypographyPresetId, ReaderTypographyPreset>;
  selectedPreset: ReaderTypographyPresetId | "custom";
  expanded: boolean;
  onToggleExpanded: () => void;
  onPresetChange: (value: ReaderTypographyPresetId | "custom") => void;
  onCustomTypographyChange: (patch: Partial<ReaderTypographySettings>) => void;
  onIndentChange: (value: number) => void;
  onTextAlignChange: (value: ReaderWorkspace["preference"]["textAlign"]) => void;
  onTextAlignLastChange: (value: ReaderWorkspace["preference"]["textAlignLast"]) => void;
}) {
  return (
    <SettingGroup>
      <SettingExpandableSelectRow label="排版" expanded={expanded} toggleLabel="展开排版细节" onToggleExpanded={onToggleExpanded}>
        <TypographyPresetSelect value={selectedPreset} presets={presets} onValueChange={onPresetChange} />
      </SettingExpandableSelectRow>

      {expanded && (
        <div className="grid gap-3">
          <SettingStepper
            label="字距"
            value={`${preference.letterSpacing.toFixed(1)}px`}
            onLess={() => onCustomTypographyChange({ letterSpacing: Number(Math.max(-1, preference.letterSpacing - 0.2).toFixed(1)) })}
            onMore={() => onCustomTypographyChange({ letterSpacing: Number(Math.min(4, preference.letterSpacing + 0.2).toFixed(1)) })}
          />
          <SettingStepper
            label="行距"
            value={preference.lineHeight.toFixed(1)}
            onLess={() => onCustomTypographyChange({ lineHeight: Number(Math.max(1.2, preference.lineHeight - 0.1).toFixed(1)) })}
            onMore={() => onCustomTypographyChange({ lineHeight: Number(Math.min(3, preference.lineHeight + 0.1).toFixed(1)) })}
          />
          <SettingStepper
            label="段距"
            value={`${preference.paragraphSpacing}px`}
            onLess={() => onCustomTypographyChange({ paragraphSpacing: Math.max(0, preference.paragraphSpacing - 4) })}
            onMore={() => onCustomTypographyChange({ paragraphSpacing: Math.min(72, preference.paragraphSpacing + 4) })}
          />
          <SettingSelectRow label="缩进">
            <Select value={String(preference.textIndent)} onValueChange={value => onIndentChange(Number(value))}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0</SelectItem>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="4">4</SelectItem>
              </SelectContent>
            </Select>
          </SettingSelectRow>
          <SettingSelectRow label="对齐">
            <Select value={preference.textAlign} onValueChange={value => onTextAlignChange(value as ReaderWorkspace["preference"]["textAlign"])}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="start">左对齐</SelectItem>
                <SelectItem value="justify">两端对齐</SelectItem>
              </SelectContent>
            </Select>
          </SettingSelectRow>
          <SettingSelectRow label="末行">
            <Select value={preference.textAlignLast} onValueChange={value => onTextAlignLastChange(value as ReaderWorkspace["preference"]["textAlignLast"])}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">默认</SelectItem>
                <SelectItem value="start">靠左</SelectItem>
                <SelectItem value="justify">两端</SelectItem>
              </SelectContent>
            </Select>
          </SettingSelectRow>
        </div>
      )}
    </SettingGroup>
  );
}

export function MarginSettings({
  preference,
  presets,
  selectedPreset,
  expanded,
  onToggleExpanded,
  onPresetChange,
  onCustomPaddingChange,
}: {
  preference: ReaderWorkspace["preference"];
  presets: Record<ReaderMarginPresetId, ReaderMarginPreset>;
  selectedPreset: ReaderMarginPresetId | "custom";
  expanded: boolean;
  onToggleExpanded: () => void;
  onPresetChange: (value: ReaderMarginPresetId | "custom") => void;
  onCustomPaddingChange: (patch: Partial<ReaderMarginPadding>) => void;
}) {
  return (
    <div className="space-y-3">
      <SettingExpandableSelectRow label="边距" expanded={expanded} toggleLabel="展开边距细节" onToggleExpanded={onToggleExpanded}>
        <MarginPresetSelect value={selectedPreset} presets={presets} onValueChange={onPresetChange} />
      </SettingExpandableSelectRow>

      {expanded && (
        <div className="grid gap-3">
          <SettingStepper
            label="上"
            value={`${preference.paddingTop}px`}
            onLess={() => onCustomPaddingChange({ paddingTop: Math.max(0, preference.paddingTop - 8) })}
            onMore={() => onCustomPaddingChange({ paddingTop: Math.min(160, preference.paddingTop + 8) })}
          />
          <SettingStepper
            label="下"
            value={`${preference.paddingBottom}px`}
            onLess={() => onCustomPaddingChange({ paddingBottom: Math.max(0, preference.paddingBottom - 8) })}
            onMore={() => onCustomPaddingChange({ paddingBottom: Math.min(200, preference.paddingBottom + 8) })}
          />
          <SettingStepper
            label="左"
            value={`${preference.paddingLeft}px`}
            onLess={() => onCustomPaddingChange({ paddingLeft: Math.max(0, preference.paddingLeft - 8) })}
            onMore={() => onCustomPaddingChange({ paddingLeft: Math.min(320, preference.paddingLeft + 8) })}
          />
          <SettingStepper
            label="右"
            value={`${preference.paddingRight}px`}
            onLess={() => onCustomPaddingChange({ paddingRight: Math.max(0, preference.paddingRight - 8) })}
            onMore={() => onCustomPaddingChange({ paddingRight: Math.min(320, preference.paddingRight + 8) })}
          />
        </div>
      )}
    </div>
  );
}

function TypographyPresetSelect({
  value,
  presets,
  onValueChange,
}: {
  value: ReaderTypographyPresetId | "custom";
  presets: Record<ReaderTypographyPresetId, ReaderTypographyPreset>;
  onValueChange: (value: ReaderTypographyPresetId | "custom") => void;
}) {
  return (
    <Select value={value} onValueChange={nextValue => onValueChange(nextValue as ReaderTypographyPresetId | "custom")}>
      <SelectTrigger className="flex-1">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(presets).map(preset => (
          <SelectItem key={preset.id} value={preset.id}>
            {preset.label}
          </SelectItem>
        ))}
        <SelectItem value="custom">自定义</SelectItem>
      </SelectContent>
    </Select>
  );
}

function MarginPresetSelect({
  value,
  presets,
  onValueChange,
}: {
  value: ReaderMarginPresetId | "custom";
  presets: Record<ReaderMarginPresetId, ReaderMarginPreset>;
  onValueChange: (value: ReaderMarginPresetId | "custom") => void;
}) {
  return (
    <Select value={value} onValueChange={nextValue => onValueChange(nextValue as ReaderMarginPresetId | "custom")}>
      <SelectTrigger className="flex-1">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.values(presets).map(preset => (
          <SelectItem key={preset.id} value={preset.id}>
            {preset.label}
          </SelectItem>
        ))}
        <SelectItem value="custom">自定义</SelectItem>
      </SelectContent>
    </Select>
  );
}
